/* eslint-disable max-lines */
import buildUrl from 'build-url';
import { capitalize } from '../../../../common/formatting/formattingService';
import { Config } from '../../../../common/types/config';
import { getEntityByUrlKey, mapEntityToBreadCrumbItem } from '../../../redux/category/categoriesNormalizer';
import { NormalizedEntitiesAndUrlKeys, SeoCategoryBreadCrumbItem } from '../../../redux/category/types';
import { AppliedFilter, FilterType, ListingPageUrlState } from '../../../redux/products/types';
import { SeoData } from '../../../redux/seo/types';
import { getNewProductsUrl } from '../../../urlService';
import { BellaTranslator } from '../../useTranslator';
import { convertListingPageStateToUrl } from '../listingPageUrlService';
import { PageType } from '../../UrlManager/types';
import { SeoListItem } from '../../../components/Generic/SeoHead/SeoBreadcrumbScript';
import { PdpSeoData } from '../../../redux/productdetail/types';
import { Dictionary } from '../../../../../types/globals';
import { AppRouteChildrenProps } from '../../useRouter';
import {
    BRAND_CATEGORIES_FILTER_LENGTH_FOR_OTHER_ACTIVE,
    BREAD_CRUMB_FILTER_WHITELIST,
    BREADCRUMBS_PATH_FILTERS_UPPER_LIMIT,
    INDEXABLE_FILTER_SELECTION_LIMIT,
    INDEXABLE_PAGE_UPPER_LIMIT,
    INDEXABLE_PATH_FILTERS_SUM_UPPER_LIMIT,
    SEO_CONSTANTS,
} from './constant';
import { BreadCrumbStateOptions } from './types';

export const buildDefaultAvailableIn = (pageType: PageType, config: Config) => {
    const { languages = {} } = config;
    const availableIn = {};
    if (pageType === PageType.NEWPRODUCTS) {
        Object.keys(languages || {}).forEach(key => {
            availableIn[languages[key]] = getNewProductsUrl();
        });
    }
    return availableIn;
};

export const getDefaultSeoData = (type: PageType, t: BellaTranslator, config: Config): SeoData => {
    const seoDefault: SeoData = {
        seoTitle: '',
        seoDescription: '',
        seoText: '',
        mobilePageHeader: '',
        seoHeadline: '',
        availableIn: buildDefaultAvailableIn(type, config),
        similarPages: [],
        seoSlider: null,
    };

    if (type === PageType.NEWPRODUCTS) {
        seoDefault.seoTitle = t('New Arrivals');
        seoDefault.seoDescription = t('New Arrivals');
    }

    if (type === PageType.ALLPRODUCTS) {
        seoDefault.seoTitle = t('Search results');
        seoDefault.seoDescription = t('Search results');
    }

    return seoDefault;
};

const getPathFilterLengths = (appliedFilters: AppliedFilter[]) =>
    appliedFilters.reduce(
        (acc, filter) => {
            acc[filter.type] = filter.values.length;
            return acc;
        },
        { brands: 0, materials: 0, colors: 0, styles: 0, sizes: 0, categories: 0, pattern: 0, form: 0, pillowType: 0 }
    );

type PathFilterSizes = ReturnType<typeof getPathFilterLengths>;

export type PartialRouterContext = Pick<AppRouteChildrenProps, 'host' | 'location'>;

const isFollowFromIndexableFilters = (lengths: PathFilterSizes) =>
    lengths.brands === 0 &&
    lengths.materials <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.colors <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.styles <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.sizes <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.pattern <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.form <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.pillowType <= INDEXABLE_FILTER_SELECTION_LIMIT;
const isFollowFromIndexableSum = ([...lengths]: Array<number>) => {
    const indexableSum = lengths.reduce((acc, number) => acc + number, 0);
    return indexableSum < INDEXABLE_FILTER_SELECTION_LIMIT;
};
const createSeoDecisionObject = (urlState: ListingPageUrlState) => {
    let isFollow = false;
    const lengths = getPathFilterLengths(urlState.appliedFilters);
    const indexableFiltersSum = lengths.styles + lengths.colors + lengths.materials + lengths.sizes;

    switch (urlState.listingPageType) {
        case PageType.ALLPRODUCTS:
            return { isFollow, indexableFiltersSum };
        case PageType.NEWPRODUCTS: {
            isFollow = isFollowFromIndexableSum([indexableFiltersSum, lengths.brands, lengths.categories]);
            return { isFollow, indexableFiltersSum };
        }
        case PageType.BRANDS: {
            if (lengths.categories === BRAND_CATEGORIES_FILTER_LENGTH_FOR_OTHER_ACTIVE) {
                isFollow = isFollowFromIndexableFilters(lengths);
                return { isFollow, indexableFiltersSum };
            }
            isFollow = isFollowFromIndexableSum([indexableFiltersSum, lengths.brands]);
            return { isFollow, indexableFiltersSum };
        }
        case PageType.LANDING: {
            if (lengths.categories > 0) {
                return { isFollow, indexableFiltersSum };
            }
            return { isFollow: isFollowFromIndexableFilters(lengths), indexableFiltersSum };
        }
        default: {
            isFollow = isFollowFromIndexableFilters(lengths);
            return { isFollow, indexableFiltersSum };
        }
    }
};

const getLastSeoBreadcrumbItem = (
    normalized: NormalizedEntitiesAndUrlKeys,
    urlState: ListingPageUrlState,
    seoPageMeta: { pageTitle: string; urlTitle: string }
) => {
    const { entities = {} } = normalized || ({} as NormalizedEntitiesAndUrlKeys);
    const { pageTitle, urlTitle } = seoPageMeta;
    const { listingPageKey, listingPageType } = urlState;
    let lastSeoBreadcrumbItem: SeoCategoryBreadCrumbItem | null | undefined = null;
    let listingPageStateForSeoBreadcrumb = urlState;
    let seoBreadCrumbHomePageOffset = 1;

    switch (listingPageType) {
        case PageType.BRANDS: {
            lastSeoBreadcrumbItem = {
                name: pageTitle,
                url: urlState.listingPageKey,
                parent: {
                    name: urlTitle,
                    url: '',
                    parent: null,
                    depthLevel: ++seoBreadCrumbHomePageOffset,
                },
                depthLevel: ++seoBreadCrumbHomePageOffset,
            };
            listingPageStateForSeoBreadcrumb = {
                ...urlState,
                appliedFilters: [],
                listingPageType,
            };
            break;
        }
        case PageType.CATEGORY: {
            const entity = getEntityByUrlKey(normalized, listingPageKey);
            lastSeoBreadcrumbItem = mapEntityToBreadCrumbItem(entities, entity);
            listingPageStateForSeoBreadcrumb = {
                ...urlState,
                appliedFilters: [],
                listingPageType,
            };
            break;
        }
        default:
            break;
    }

    return { lastSeoBreadcrumbItem, listingPageStateForSeoBreadcrumb };
};

export const buildListForSeoBreadcrumbs = (
    normalized: NormalizedEntitiesAndUrlKeys,
    urlState: ListingPageUrlState,
    seoPage: BreadCrumbStateOptions
) => {
    const { host, pageTitle, urlTitle = '' } = seoPage;
    const {
        lastSeoBreadcrumbItem: seoBreadCrumbItem,
        listingPageStateForSeoBreadcrumb,
    } = getLastSeoBreadcrumbItem(normalized, urlState, { pageTitle, urlTitle });
    let lastSeoBreadcrumbItem = seoBreadCrumbItem;
    const breadcrumbList: SeoListItem[] = [];
    let showSeoScript = false;
    while (lastSeoBreadcrumbItem) {
        showSeoScript = true;
        breadcrumbList.unshift({
            '@type': 'ListItem',
            position: lastSeoBreadcrumbItem.depthLevel,
            name: lastSeoBreadcrumbItem.name,
            item: convertListingPageStateToUrl(
                { ...listingPageStateForSeoBreadcrumb, listingPageKey: lastSeoBreadcrumbItem.url },
                host
            ),
        });
        lastSeoBreadcrumbItem = lastSeoBreadcrumbItem.parent;
    }
    breadcrumbList.unshift({
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: buildUrl(host),
    });

    // does not violate seo path filters combo
    const { isFollow, indexableFiltersSum } = createSeoDecisionObject(urlState);

    // at least one path filter is selected
    const shouldAddToBreadcrumb = indexableFiltersSum <= BREADCRUMBS_PATH_FILTERS_UPPER_LIMIT && isFollow;
    if (shouldAddToBreadcrumb) {
        const pathFilters = urlState.appliedFilters.filter(filter => filter.type !== FilterType.query);
        pathFilters.forEach(filter => {
            const isInWhitelist = BREAD_CRUMB_FILTER_WHITELIST.includes(filter.name);
            const name = filter.values ? capitalize(filter.values[0]) : '';

            if (isInWhitelist && name) {
                const url = convertListingPageStateToUrl({ ...urlState, appliedFilters: [...pathFilters] }, host);
                breadcrumbList.push({
                    '@type': 'ListItem',
                    position: breadcrumbList.length + 1,
                    name,
                    item: url,
                });
            }
        });
    }
    return { showSeoScript, breadcrumbList };
};

export const computeSeoIndexableAndFollow = (listingPageState: ListingPageUrlState) => {
    const { appliedFilters = [], page = 1 } = listingPageState;
    const queryFilters = appliedFilters.filter(filter => filter.type === FilterType.query);

    const isQueryFiltersEmpty = queryFilters.length === 0;
    const { isFollow, indexableFiltersSum } = createSeoDecisionObject(listingPageState);

    let indexContent = SEO_CONSTANTS.noIndexable;
    const hasNoActiveFiltersViolation = indexableFiltersSum <= INDEXABLE_PATH_FILTERS_SUM_UPPER_LIMIT;
    const isPageIndexableFromMultipleFilter = indexableFiltersSum <= 1 ? true : page === INDEXABLE_PAGE_UPPER_LIMIT;

    if (hasNoActiveFiltersViolation && isPageIndexableFromMultipleFilter && isFollow && isQueryFiltersEmpty) {
        indexContent = SEO_CONSTANTS.indexable;
    }
    return indexContent;
};

export const buildPageNumberQueryString = (pageNumber: number) => (pageNumber > 1 ? `?page=${pageNumber}` : '');

export const buildPaginationHeadlinkObject = (
    rel: string,
    urlStateForSeo: {
        totalCount: number;
        listingPageSize: number;
        urlState: ListingPageUrlState;
    },
    host: string
) => {
    const { totalCount, listingPageSize, urlState } = urlStateForSeo;
    let number = urlState.page;
    let shouldrender = true;
    switch (rel) {
        case SEO_CONSTANTS.prevRelKeyLink: {
            --number;
            shouldrender = number > 0;
            break;
        }
        case SEO_CONSTANTS.nextRelKeyLink: {
            ++number;
            const totalPages = Math.ceil(totalCount / listingPageSize);
            shouldrender = urlState.page < totalPages;
            break;
        }
        default: {
            break;
        }
    }
    const copyUrlState = { ...urlState, page: number };
    return shouldrender ? { rel, href: convertListingPageStateToUrl(copyUrlState, host) } : {};
};

const hasPathFilter = (urlState: ListingPageUrlState) => {
    const { appliedFilters } = urlState;
    return appliedFilters.filter(filter => filter.type !== FilterType.query).length;
};

const createHrefLangPLPUrlState = (urlState: ListingPageUrlState, availableIn: string) => {
    const isPLPWithCategoryFilter = urlState.listingPageType === PageType.NEWPRODUCTS;

    const baseHrefLangState = {
        ...urlState,
        page: hasPathFilter(urlState) ? 1 : urlState.page,
        sortingParams: {},
        search: undefined,
    };
    if (isPLPWithCategoryFilter) {
        baseHrefLangState.appliedFilters = [];
        const categoryFilters = urlState.appliedFilters.filter(filter => filter.type === FilterType.categories);
        const categoryFilter = {
            name: '',
            type: FilterType.categories,
            ...categoryFilters.pop(),
            values: [availableIn],
        };
        baseHrefLangState.appliedFilters.push(categoryFilter);
    } else {
        baseHrefLangState.listingPageKey = availableIn;
        baseHrefLangState.appliedFilters = [];
    }
    return baseHrefLangState;
};

const doesHrefLangContainsLang = (hrefLangsDomains: { [key: string]: string } = {}, lang: string) =>
    Object.keys(hrefLangsDomains || {}).some(key => key === lang || key.indexOf(lang) > -1);

const getHrefDomain = (config: Config, availableIn: string) => {
    if (doesHrefLangContainsLang(config.hrefLangsDomains, availableIn)) {
        return config.hrefLangsDomains[availableIn];
    }
    return config.links.url;
};

export const buildCanonicalObject = (urlState: ListingPageUrlState, seoData: SeoData, host: string): object => {
    const { page } = urlState;
    const copyUrlState = {
        ...urlState,
        page: hasPathFilter(urlState) ? 1 : page,
        search: undefined,
        sortingParams: {},
    };

    const href = seoData.canonical ? seoData.canonical : convertListingPageStateToUrl(copyUrlState, host);
    return { rel: SEO_CONSTANTS.canonicalKeyLink, href };
};

const getAvailableCountriesWithAustria = (availableIn: Dictionary<string>): Dictionary<string> => {
    return {
        ...availableIn,
        at: availableIn.de,
    };
};

export const hrefLang = (seoData: SeoData | undefined, config: Config, urlState: ListingPageUrlState) => {
    const hasAvailableIn = (Object.keys((seoData || { availableIn: {} }).availableIn) || []).length > 0;
    const { availableCountryCodes } = config;

    if (seoData && hasAvailableIn) {
        const canonicalLinkObject = buildCanonicalObject(urlState, seoData, config.links.url);

        const availableInWithAustria = getAvailableCountriesWithAustria(seoData.availableIn);
        let defaultDEHrefLang = {};

        const alternateHrefs = Object.keys(availableInWithAustria).map(country => {
            const hrefLangCountry = availableCountryCodes[country];
            const availableIn = availableInWithAustria[country];
            const seoHrefLangPLPUrlState = createHrefLangPLPUrlState(urlState, availableIn);

            defaultDEHrefLang = {
                rel: SEO_CONSTANTS.alternateKeyLink,
                hrefLang: 'de',
                href: convertListingPageStateToUrl(seoHrefLangPLPUrlState, getHrefDomain(config, country)),
            };

            return {
                rel: SEO_CONSTANTS.alternateKeyLink,
                hrefLang: hrefLangCountry,
                href: convertListingPageStateToUrl(seoHrefLangPLPUrlState, getHrefDomain(config, country)),
            };
        });
        return [canonicalLinkObject, defaultDEHrefLang, ...alternateHrefs];
    }

    return [];
};

export const pdpHrefLang = (seoData: PdpSeoData, config: Config, router: PartialRouterContext) => {
    const hasAvailableIn = (Object.keys((seoData || { availableIn: {} }).availableIn) || []).length > 0;
    const {
        availableCountryCodes,
        links: { url },
    } = config;
    const {
        location: { pathname },
    } = router;

    if (seoData && hasAvailableIn) {
        const availableInWithAustria = getAvailableCountriesWithAustria(seoData.availableIn);
        const defaultLinks = [
            {
                rel: SEO_CONSTANTS.canonicalKeyLink,
                href: buildUrl(url, { path: pathname }),
            },
            {
                rel: SEO_CONSTANTS.alternateKeyLink,
                hrefLang: 'de',
                href: buildUrl(getHrefDomain(config, 'de'), { path: availableInWithAustria.de }),
            },
        ];

        const alternateHrefs = Object.keys(availableInWithAustria).map(country => {
            const hrefLangCountry = availableCountryCodes[country];
            const countrySpecificPathname = availableInWithAustria[country];

            return {
                rel: SEO_CONSTANTS.alternateKeyLink,
                hrefLang: hrefLangCountry,
                href: buildUrl(getHrefDomain(config, country), { path: countrySpecificPathname }),
            };
        });
        return [...defaultLinks, ...alternateHrefs];
    }

    return [];
};

export const homePageHrefLang = (config: Config) => {
    const canonicalHref = config.links.url;
    const canonicalLinkObject = { rel: SEO_CONSTANTS.canonicalKeyLink, href: canonicalHref };
    const defaultDEHrefLang = {
        rel: SEO_CONSTANTS.alternateKeyLink,
        hrefLang: 'de',
        href: config.links.url,
    };

    const alternateHrefs = Object.keys(config.availableCountryCodes).map(country => {
        const hrefLangCountry = config.availableCountryCodes[country];
        return {
            rel: SEO_CONSTANTS.alternateKeyLink,
            hrefLang: hrefLangCountry,
            href: buildUrl(getHrefDomain(config, country), { path: '/' }),
        };
    });

    return [canonicalLinkObject, defaultDEHrefLang, ...alternateHrefs];
};
